import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, database } from "../../firebase";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { ref, set, get, update } from "firebase/database";
import useIsMobile from "../../hooks/useIsMobile";
import SignupInputFields from "./SignupInputFields";
import getErrorMessage from "../../utils/errorUtils";

const SignUpForm = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    department: "",
    institutionName: "",
    number: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  const handleChange = (field) => (value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const { firstName, lastName, department, institutionName, number, email, password, confirmPassword } = formData;

    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError("Please fill in all required fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password should be at least 6 characters.");
      return;
    }

    setError("");
    setIsFetching(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);

      const userRef = ref(database, `users/${userCredential.user.uid}`);
      const cleanValue = (v) => (v == null ? "" : v);

      await set(userRef, {
        id: userCredential.user.uid,
        firstName: cleanValue(firstName),
        lastName: cleanValue(lastName),
        department: cleanValue(department),
        institutionName: cleanValue(institutionName),
        number: cleanValue(number),
        email: cleanValue(email),
        cases: {
          0: "-O9Di_x-F7YpJFGaY-1d",
          1: "-OU_VsesW8frMeK-OoDb",
          2: "-OJbrL3KFIO2yBhEtDe5",
        },
      });

      navigate("/login");
    } catch (error) {
      setError(getErrorMessage(error.message));
    } finally {
      setIsFetching(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");

    try {
      setIsFetching(true);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const uid = user.uid;

      const googleUserData = {
        id: uid,
        email: user.email,
        firstName: user.displayName?.split(" ")[0] || "",
        lastName: user.displayName?.split(" ").slice(1).join(" ") || "",
        number: user.phoneNumber || "",
        department: "",
        institutionName: "",
        cases: {
          0: "-O9Di_x-F7YpJFGaY-1d",
          1: "-OU_VsesW8frMeK-OoDb",
          2: "-OJbrL3KFIO2yBhEtDe5",
        },
      };

      const userRef = ref(database, `users/${uid}`);
      const snapshot = await get(userRef);

      if (!snapshot.exists()) {
        await set(userRef, googleUserData);
      } else {
        const existingData = snapshot.val();
        await update(userRef, {
          ...existingData,
          email: existingData.email || googleUserData.email,
          firstName: existingData.firstName || googleUserData.firstName,
          lastName: existingData.lastName || googleUserData.lastName,
        });
      }

      navigate("/home");
    } catch (error) {
      if (error.code === "auth/popup-closed-by-user") {
        setError("Google sign-in was canceled.");
      } else {
        setError(getErrorMessage(error.message));
      }
    } finally {
      setIsFetching(false);
    }
  };

  const pageStyle = {
    minHeight: "100vh",
    backgroundColor: "#0B0E13",
    position: "relative",
    overflow: "hidden",
  };

  const glowTop = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "700px",
    height: "300px",
    background: "radial-gradient(ellipse at center, rgba(48,166,156,0.1) 0%, transparent 70%)",
    pointerEvents: "none",
  };

  const cardStyle = {
    backgroundColor: "rgba(13,17,23,0.85)",
    border: "1px solid rgba(255,255,255,0.1)",
    backdropFilter: "blur(20px)",
    boxShadow: "0 0 0 1px rgba(48,166,156,0.1), 0 24px 48px rgba(0,0,0,0.5)",
    borderRadius: "16px",
    padding: isMobile ? "24px" : "36px",
    width: isMobile ? "100%" : "500px",
    maxHeight: "90vh",
    overflowY: "auto",
  };

  return (
    <div style={pageStyle}>
      <div style={glowTop} />
      {/* Background image overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "url('/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          opacity: 0.08,
          pointerEvents: "none",
        }}
      />

      <div
        className="flex items-center justify-center"
        style={{ minHeight: "100vh", position: "relative", zIndex: 10, padding: isMobile ? "24px 16px" : "40px 24px" }}
      >
        <div style={cardStyle}>
          {/* Brand */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold tracking-wide" style={{ color: "#30A69C" }}>
              Cardiowell
            </h1>
            <p className="text-sm mt-1" style={{ color: "#64748B" }}>
              Create your account
            </p>
          </div>

          <h2 className="text-xl font-semibold text-center mb-6" style={{ color: "#E7EBEF" }}>
            Sign Up
          </h2>

          <SignupInputFields
            formData={formData}
            handleChange={handleChange}
            onSubmit={onSubmit}
            isFetching={isFetching}
            error={error}
            handleGoogleSignIn={handleGoogleSignIn}
          />
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;
